// Analyses croisées alimentation/poids : corrélation déficit↔variation, et perte théorique vs réelle.

import { aggregateBy } from './aggregate';
import { mean, pearsonCorrelation, round } from './statistics';
import { KCAL_PER_KG_FAT } from './threshold';
import type { Granularity, WeightEntry } from './types';
import { smoothedWeight } from './weight-analytics';

export interface DailyIntake {
  date: string;
  kcal: number;
}

export interface DeficitWeightPoint {
  key: string;
  start: string;
  avgDailyDeficitKcal: number; // positif = déficit
  weightDeltaKg: number; // variation vs période précédente
}

export interface DeficitWeightAnalysis {
  points: DeficitWeightPoint[];
  correlation: number; // Pearson déficit↔variation, attendu < 0
}

// Met en regard, par période, déficit moyen et variation de poids, puis calcule leur corrélation.
export function deficitVsWeightChange(
  dailyIntake: readonly DailyIntake[],
  tdeeKcal: number,
  weights: readonly WeightEntry[],
  granularity: Granularity = 'week',
): DeficitWeightAnalysis {
  // Déficit moyen/jour par période.
  const deficitByKey = new Map<string, { start: string; deficit: number }>();
  for (const b of aggregateBy(
    dailyIntake,
    (d) => d.date,
    granularity,
    (g) => mean(g.map((x) => x.kcal)),
  )) {
    deficitByKey.set(b.key, { start: b.start, deficit: tdeeKcal - b.value });
  }

  // Poids moyen par période, puis variation vs période précédente.
  const weightBuckets = aggregateBy(
    weights,
    (w) => w.date,
    granularity,
    (g) => mean(g.map((w) => w.weightKg)),
  );

  const points: DeficitWeightPoint[] = [];
  for (let i = 1; i < weightBuckets.length; i++) {
    const cur = weightBuckets[i]!;
    const prev = weightBuckets[i - 1]!;
    const d = deficitByKey.get(cur.key);
    if (!d) continue; // pas d'apport saisi sur cette période
    points.push({
      key: cur.key,
      start: cur.start,
      avgDailyDeficitKcal: round(d.deficit),
      weightDeltaKg: round(cur.value - prev.value, 2),
    });
  }

  const correlation = pearsonCorrelation(
    points.map((p) => p.avgDailyDeficitKcal),
    points.map((p) => p.weightDeltaKg),
  );
  return { points, correlation: Number.isNaN(correlation) ? NaN : round(correlation, 3) };
}

// Perte théorique cumulée = déficit cumulé / KCAL_PER_KG_FAT (~7700 kcal/kg, Wishnofsky).
export function predictedLossSeries(
  dailyIntake: readonly DailyIntake[],
  tdeeKcal: number,
): { date: string; predictedLossKg: number }[] {
  const sorted = [...dailyIntake].sort((a, b) => a.date.localeCompare(b.date));
  let cumulativeDeficit = 0;
  return sorted.map((d) => {
    cumulativeDeficit += tdeeKcal - d.kcal;
    return { date: d.date, predictedLossKg: round(cumulativeDeficit / KCAL_PER_KG_FAT, 2) };
  });
}

// Perte réelle = poids initial lissé − poids lissé (lissage contre le bruit eau/glycogène).
export function actualLossSeries(
  weights: readonly WeightEntry[],
  window = 7,
): { date: string; actualLossKg: number }[] {
  const smoothed = smoothedWeight(weights, window);
  if (smoothed.length === 0) return [];
  const start = smoothed[0]!.smoothed;
  return smoothed.map((s) => ({ date: s.date, actualLossKg: round(start - s.smoothed, 2) }));
}
